import { create } from 'zustand';

export interface PlaylistItem {
  paragraphId: string;
  audioUrl: string | null;
  durationMs: number | null;
  speakerLabel: string | null;
}

interface PlaybackState {
  isPlaying: boolean;
  currentParagraphId: string | null;
  currentChapterId: string | null;
  playlist: PlaylistItem[];
  currentIndex: number;
  volume: number;
  rate: number;
  ambientUrl: string | null;
  ambientVolume: number;

  loadPlaylist: (chapterId: string, items: PlaylistItem[]) => void;
  play: (paragraphId?: string) => void;
  pause: () => void;
  togglePlayPause: () => void;
  next: () => void;
  previous: () => void;
  setVolume: (v: number) => void;
  setAmbientUrl: (url: string | null) => void;
  setAmbientVolume: (v: number) => void;
  setRate: (r: number) => void;
  onTrackEnded: () => void;
  stop: () => void;
}

export const usePlaybackStore = create<PlaybackState>((set, get) => ({
  isPlaying: false,
  currentParagraphId: null,
  currentChapterId: null,
  playlist: [],
  currentIndex: -1,
  volume: 1,
  rate: 1,
  ambientUrl: null,
  ambientVolume: 0.3,

  loadPlaylist: (chapterId, items) => {
    const playable = items.filter((i) => i.audioUrl);
    set({
      currentChapterId: chapterId,
      playlist: playable,
      currentIndex: playable.length > 0 ? 0 : -1,
      currentParagraphId: playable[0]?.paragraphId ?? null,
      isPlaying: false,
    });
  },

  play: (paragraphId) => {
    const state = get();
    if (paragraphId) {
      const idx = state.playlist.findIndex(
        (i) => i.paragraphId === paragraphId,
      );
      if (idx >= 0) {
        set({
          currentIndex: idx,
          currentParagraphId: state.playlist[idx]!.paragraphId,
          isPlaying: true,
        });
        return;
      }
    }
    if (state.currentIndex >= 0) {
      set({ isPlaying: true });
    }
  },

  pause: () => set({ isPlaying: false }),

  togglePlayPause: () => {
    const state = get();
    if (state.isPlaying) {
      set({ isPlaying: false });
    } else if (state.currentIndex >= 0) {
      set({ isPlaying: true });
    }
  },

  next: () => {
    const state = get();
    const nextIndex = state.currentIndex + 1;
    if (nextIndex < state.playlist.length) {
      set({
        currentIndex: nextIndex,
        currentParagraphId: state.playlist[nextIndex]!.paragraphId,
        isPlaying: true,
      });
    } else {
      set({ isPlaying: false });
    }
  },

  previous: () => {
    const state = get();
    const prevIndex = state.currentIndex - 1;
    if (prevIndex >= 0) {
      set({
        currentIndex: prevIndex,
        currentParagraphId: state.playlist[prevIndex]!.paragraphId,
        isPlaying: true,
      });
    }
  },

  setVolume: (v) => set({ volume: v }),
  setRate: (r) => set({ rate: r }),
  setAmbientUrl: (url) => set({ ambientUrl: url }),
  setAmbientVolume: (v) => set({ ambientVolume: v }),

  onTrackEnded: () => {
    get().next();
  },

  stop: () =>
    set({
      isPlaying: false,
      currentParagraphId: null,
      currentChapterId: null,
      playlist: [],
      currentIndex: -1,
    }),
}));
